import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext, API, getAuthHeaders } from '../App';
import { Navigation } from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Video, Upload, Link2, MapPin, ArrowLeft, Loader2, X, Play,
  Flame, DollarSign, Trophy, Eye, MessageSquare, Send, ThumbsUp, Clock
} from 'lucide-react';
import { toast } from 'sonner';

// Extract YouTube video ID from various URL formats
const extractYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /youtube\.com\/shorts\/([^&\s?]+)/,
  ];
  for (const p of patterns) {
    const match = url.match(p);
    if (match) return match[1];
  }
  return null;
};

export default function GoLive() {
  const { momentId } = useParams();
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Mode: post (default) or view (viewing a specific moment)
  const [mode, setMode] = useState(momentId ? 'view' : 'post');
  const [tab, setTab] = useState('upload'); // upload or youtube

  // Post form
  const [title, setTitle] = useState('');
  const [courseName, setCourseName] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [posting, setPosting] = useState(false);

  // Upload tab
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);

  // YouTube tab
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeId, setYoutubeId] = useState(null);

  // View mode
  const [moment, setMoment] = useState(null);
  const [loadingMoment, setLoadingMoment] = useState(!!momentId);
  const [commentText, setCommentText] = useState('');

  useEffect(() => { if (!loading && !user) navigate('/'); }, [loading, user, navigate]);

  // Fetch moment for view mode
  useEffect(() => {
    if (momentId && user) {
      setLoadingMoment(true);
      fetch(`${API}/golf-moments/${momentId}`, { credentials: 'include', headers: getAuthHeaders() })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) { setMoment(d); setMode('view'); } else setMode('post'); })
        .catch(() => setMode('post'))
        .finally(() => setLoadingMoment(false));
    }
  }, [momentId, user]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) { toast.error('Please select a video file'); return; }

    // Size cap 500MB (matches backend /api/golf-moments/upload)
    if (file.size > 500 * 1024 * 1024) {
      toast.error(`Video too large (${(file.size / 1024 / 1024).toFixed(0)}MB). Max size is 500MB — try trimming or compressing the clip.`);
      return;
    }

    // Check video duration (3 min max)
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      if (video.duration > 180) {
        toast.error('Video must be 3 minutes or less');
        return;
      }
      setSelectedFile(file);
      setFilePreview(url);
      setVideoDuration(Math.round(video.duration));
    };
    video.src = url;
  };

  const handleYouTubeUrl = (url) => {
    setYoutubeUrl(url);
    const id = extractYouTubeId(url);
    setYoutubeId(id);
  };

  const handlePost = async () => {
    if (tab === 'upload' && !selectedFile) { toast.error('Select a video to upload'); return; }
    if (tab === 'youtube' && !youtubeId) { toast.error('Enter a valid YouTube URL'); return; }
    setPosting(true);
    try {
      if (tab === 'upload') {
        // Upload video file
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('title', title || 'Golf Moment');
        formData.append('course_name', courseName);
        formData.append('visibility', visibility);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API}/golf-moments/upload`);
        xhr.withCredentials = true;
        const authH = getAuthHeaders();
        if (authH.Authorization) xhr.setRequestHeader('Authorization', authH.Authorization);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        };

        const result = await new Promise((resolve, reject) => {
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText));
            else {
              // Try to parse backend JSON detail; fall back to generic message
              let msg = 'Upload failed';
              try {
                const body = JSON.parse(xhr.responseText);
                if (body?.detail) msg = body.detail;
              } catch {
                if (xhr.status === 413) msg = 'Video too large. Max size is 500MB — try trimming or compressing the clip.';
                else if (xhr.status === 401) msg = 'Please log in again';
                else if (xhr.responseText && xhr.responseText.length < 200) msg = xhr.responseText;
              }
              reject(new Error(msg));
            }
          };
          xhr.onerror = () => reject(new Error('Upload failed — check your connection'));
          xhr.send(formData);
        });

        toast.success('Video posted!');
        navigate('/dashboard');
      } else {
        // Post YouTube link
        const res = await fetch(`${API}/golf-moments/youtube`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify({
            youtube_url: youtubeUrl,
            youtube_id: youtubeId,
            title: title || 'Golf Moment',
            course_name: courseName,
            visibility,
          }),
        });
        if (res.ok) {
          toast.success('Video posted!');
          navigate('/dashboard');
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Failed to post');
        }
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to post video');
    } finally {
      setPosting(false);
      setUploadProgress(0);
    }
  };

  const sendComment = async () => {
    if (!commentText.trim() || !moment) return;
    try {
      const res = await fetch(`${API}/golf-moments/${moment.moment_id}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ text: commentText.trim() }),
      });
      if (res.ok) {
        const comment = await res.json();
        setMoment(prev => ({ ...prev, comments: [...(prev.comments || []), comment] }));
        setCommentText('');
      }
    } catch {}
  };

  const sendReaction = async (type) => {
    if (!moment) return;
    try {
      await fetch(`${API}/golf-moments/${moment.moment_id}/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ reaction: type }),
      });
      setMoment(prev => ({
        ...prev,
        reactions: { ...prev.reactions, [type]: (prev.reactions?.[type] || 0) + 1 }
      }));
    } catch {}
  };

  if (loading || loadingMoment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green" />
      </div>
    );
  }

  // ===== VIEW MODE =====
  if (mode === 'view' && moment) {
    return (
      <div className="min-h-screen bg-black" data-testid="moment-view">
        {/* Video */}
        <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
          {moment.media_type === 'youtube' ? (
            <iframe
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube.com/embed/${moment.youtube_id}?autoplay=1&rel=0`}
              allow="autoplay; encrypted-media" allowFullScreen
            />
          ) : (
            <video className="absolute inset-0 w-full h-full object-cover" src={moment.media_url} controls autoPlay playsInline />
          )}
        </div>

        {/* Info + Actions */}
        <div className="bg-bundle-cream min-h-[50vh] rounded-t-3xl -mt-4 relative z-10 p-4">
          <button onClick={() => navigate(-1)} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-bundle-cream-dark flex items-center justify-center">
            <X className="w-4 h-4 text-gray-500" />
          </button>

          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-bundle-green flex items-center justify-center text-white font-bold text-sm">
              {moment.user_name?.[0] || '?'}
            </div>
            <div>
              <p className="font-bold text-bundle-green text-sm">{moment.user_name}</p>
              {moment.course_name && <p className="text-xs text-gray-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{moment.course_name}</p>}
            </div>
            <div className="ml-auto flex items-center gap-1 text-gray-400 text-xs">
              <Clock className="w-3 h-3" /> {new Date(moment.created_at).toLocaleDateString()}
            </div>
          </div>

          <p className="font-bold text-bundle-green mb-3">{moment.title}</p>

          {/* Reactions */}
          <div className="flex items-center gap-2 mb-4">
            {[
              { key: 'fire', emoji: '🔥', count: moment.reactions?.fire || 0 },
              { key: 'money', emoji: '💰', count: moment.reactions?.money || 0 },
              { key: 'trophy', emoji: '🏆', count: moment.reactions?.trophy || 0 },
            ].map(r => (
              <button key={r.key} onClick={() => sendReaction(r.key)} data-testid={`react-${r.key}`}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-bundle-cream-dark text-sm active:scale-95 transition-transform shadow-sm">
                <span>{r.emoji}</span>
                <span className="text-xs font-bold text-gray-600">{r.count}</span>
              </button>
            ))}
            <div className="ml-auto flex items-center gap-1 text-gray-400 text-xs">
              <Eye className="w-3.5 h-3.5" /> {moment.view_count || 0} views
            </div>
          </div>

          {/* Game Buttons */}
          {moment.game_id && (
            <div className="flex gap-2 mb-4">
              <button onClick={() => navigate(`/live-round/${moment.game_id}`)} className="flex-1 py-2 bg-white border border-bundle-cream-dark text-bundle-green font-bold text-xs rounded-xl">View Game</button>
              <button onClick={() => navigate(`/live-round/${moment.game_id}`)} className="flex-1 py-2 bg-bundle-green text-white font-bold text-xs rounded-xl">Join Game</button>
            </div>
          )}

          {/* Comments */}
          <div className="mb-20">
            <p className="text-xs font-bold text-gray-500 uppercase mb-2">Comments ({(moment.comments || []).length})</p>
            <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
              {(moment.comments || []).map((c, i) => (
                <div key={c.comment_id || i} className="flex gap-2">
                  <div className="w-6 h-6 rounded-full bg-bundle-green/10 flex items-center justify-center text-bundle-green text-[10px] font-bold flex-shrink-0">{c.user_name?.[0]}</div>
                  <div>
                    <span className="text-xs font-bold text-bundle-green">{c.user_name}</span>
                    <span className="text-xs text-gray-600 ml-1">{c.text}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="text" placeholder="Add a comment..." value={commentText} onChange={e => setCommentText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') sendComment(); }}
                data-testid="comment-input"
                className="flex-1 bg-white border border-bundle-cream-dark rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-bundle-green/40" />
              <button onClick={sendComment} disabled={!commentText.trim()} data-testid="send-comment-btn"
                className="w-10 h-10 rounded-xl bg-bundle-green flex items-center justify-center disabled:opacity-50">
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== POST MODE =====
  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav" data-testid="post-moment">
      <Navigation user={user} />
      <div className="max-w-md mx-auto px-4 pt-20">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1 text-bundle-green text-sm mb-4 hover:underline">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </button>

        <div className="text-center mb-5">
          <div className="w-14 h-14 mx-auto mb-2 rounded-full bg-bundle-green/10 flex items-center justify-center">
            <Video className="w-7 h-7 text-bundle-green" />
          </div>
          <h1 className="font-heading font-bold text-xl text-bundle-green" data-testid="page-title">Course Cam</h1>
          <p className="text-gray-400 text-xs mt-1">Upload a video or share a YouTube link</p>
        </div>

        {/* Tab Toggle */}
        <div className="flex bg-white rounded-xl border border-bundle-cream-dark p-1 mb-5 shadow-sm" data-testid="post-tabs">
          <button onClick={() => setTab('upload')} data-testid="tab-upload"
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold transition-all ${
              tab === 'upload' ? 'bg-bundle-green text-white shadow-md' : 'text-gray-500'
            }`}>
            <Upload className="w-4 h-4" /> Upload Video
          </button>
          <button onClick={() => setTab('youtube')} data-testid="tab-youtube"
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold transition-all ${
              tab === 'youtube' ? 'bg-red-500 text-white shadow-md' : 'text-gray-500'
            }`}>
            <Play className="w-4 h-4" /> YouTube Link
          </button>
        </div>

        {/* Upload Tab */}
        {tab === 'upload' && (
          <div className="mb-5">
            <input ref={fileInputRef} type="file" accept="video/*" onChange={handleFileSelect} className="hidden" />
            <input ref={cameraInputRef} type="file" accept="video/*" capture="environment" onChange={handleFileSelect} className="hidden" />
            {!selectedFile ? (
              <div className="space-y-2">
                <button onClick={() => fileInputRef.current?.click()} data-testid="upload-area"
                  className="w-full h-28 rounded-2xl border-2 border-dashed border-bundle-green/30 bg-bundle-green/5 flex flex-col items-center justify-center gap-1.5 hover:bg-bundle-green/10 hover:border-bundle-green/50 active:scale-[0.98] transition-all">
                  <div className="w-10 h-10 rounded-full bg-bundle-green/10 flex items-center justify-center">
                    <Upload className="w-5 h-5 text-bundle-green" />
                  </div>
                  <p className="text-sm font-bold text-bundle-green">Choose from Gallery</p>
                  <p className="text-[10px] text-gray-400">3 min max — MP4, MOV, WebM</p>
                </button>
                <button onClick={() => cameraInputRef.current?.click()} data-testid="camera-record"
                  className="w-full h-14 rounded-2xl bg-white border border-bundle-cream-dark flex items-center justify-center gap-2 hover:border-bundle-green/30 active:scale-[0.98] transition-all shadow-sm">
                  <Video className="w-5 h-5 text-red-500" />
                  <span className="text-sm font-bold text-bundle-green">Record with Camera</span>
                </button>
              </div>
            ) : (
              <div className="relative rounded-2xl overflow-hidden bg-black" data-testid="video-preview">
                <video src={filePreview} className="w-full h-44 object-cover" controls muted playsInline />
                <button onClick={() => { setSelectedFile(null); setFilePreview(null); }}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center">
                  <X className="w-4 h-4 text-white" />
                </button>
                <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/50 rounded-full text-white text-[10px]">
                  {Math.floor(videoDuration / 60)}:{String(videoDuration % 60).padStart(2, '0')}
                </div>
              </div>
            )}
            {/* Upload progress */}
            {posting && uploadProgress > 0 && (
              <div className="mt-2">
                <div className="h-1.5 bg-bundle-cream-dark rounded-full overflow-hidden">
                  <div className="h-full bg-bundle-green rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
                <p className="text-[10px] text-gray-400 text-right mt-0.5">{uploadProgress}%</p>
              </div>
            )}
          </div>
        )}

        {/* YouTube Tab */}
        {tab === 'youtube' && (
          <div className="mb-5">
            <div className="relative">
              <Link2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input data-testid="youtube-url-input" placeholder="Paste YouTube URL..." value={youtubeUrl} onChange={e => handleYouTubeUrl(e.target.value)}
                className="pl-10 bg-white" />
            </div>
            {youtubeId && (
              <div className="mt-3 rounded-2xl overflow-hidden bg-black shadow-md" data-testid="youtube-preview">
                <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
                  <iframe className="absolute inset-0 w-full h-full" src={`https://www.youtube.com/embed/${youtubeId}`}
                    allow="encrypted-media" allowFullScreen />
                </div>
              </div>
            )}
            {youtubeUrl && !youtubeId && (
              <p className="text-xs text-red-500 mt-2">Could not detect YouTube video — check the URL</p>
            )}
          </div>
        )}

        {/* Title */}
        <div className="mb-4">
          <Label className="text-sm text-gray-600">Title</Label>
          <Input data-testid="moment-title-input" placeholder='e.g. "Birdie on 7 — What a putt!"' value={title} onChange={e => setTitle(e.target.value)} className="mt-1 bg-white" />
        </div>

        {/* Course */}
        <div className="mb-4">
          <Label className="text-sm text-gray-600">Course (optional)</Label>
          <div className="relative mt-1">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input data-testid="moment-course-input" placeholder="Lake Presidential Golf Club" value={courseName} onChange={e => setCourseName(e.target.value)} className="pl-9 bg-white" />
          </div>
        </div>

        {/* Visibility */}
        <div className="mb-5">
          <Label className="text-sm text-gray-600">Visibility</Label>
          <div className="flex gap-2 mt-1">
            {[
              { value: 'public', label: 'Public', desc: 'All members' },
              { value: 'private', label: 'Private', desc: 'Group only' },
            ].map(opt => (
              <button key={opt.value} data-testid={`visibility-${opt.value}`}
                onClick={() => setVisibility(opt.value)}
                className={`flex-1 p-3 rounded-xl border text-center transition-all ${
                  visibility === opt.value
                    ? 'bg-bundle-green/5 border-bundle-green/30 text-bundle-green'
                    : 'bg-white border-bundle-cream-dark text-gray-500'
                }`}>
                <p className="font-bold text-sm">{opt.label}</p>
                <p className="text-[10px] text-gray-400">{opt.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Post Button */}
        <Button data-testid="post-moment-btn" onClick={handlePost}
          disabled={posting || (tab === 'upload' && !selectedFile) || (tab === 'youtube' && !youtubeId)}
          className="w-full py-5 bg-bundle-green hover:bg-bundle-green-dark text-white font-bold text-sm rounded-xl shadow-lg shadow-bundle-green/20 disabled:opacity-50">
          {posting ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> {tab === 'upload' ? `Uploading ${uploadProgress}%...` : 'Posting...'}</> : <><Video className="w-5 h-5 mr-2" /> Post Moment</>}
        </Button>
      </div>
    </div>
  );
}
